import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export type SlugStatus = 'idle' | 'checking' | 'free' | 'taken';

interface SlugFieldProps {
  source: string;
  value: string;
  onChange: (next: string) => void;
  excludeId?: string;
  onStatusChange?: (status: SlugStatus) => void;
  /**
   * When true, the field starts in "manually edited" mode and never
   * auto-derives from `source`. Use on edit forms where the slug is
   * pre-populated and shouldn't be silently rewritten by title edits.
   */
  initialEdited?: boolean;
}

export default function SlugField({
  source,
  value,
  onChange,
  excludeId,
  onStatusChange,
  initialEdited = false,
}: SlugFieldProps) {
  const [edited, setEdited] = useState(initialEdited);
  const [status, setStatus] = useState<SlugStatus>('idle');

  // Auto-derive from source until the field is manually touched.
  useEffect(() => {
    if (!edited) {
      const derived = slugify(source);
      if (derived !== value) onChange(derived);
    }
  }, [source, edited, value, onChange]);

  // Surface status to the parent for submit-disable.
  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  // Debounced uniqueness check.
  useEffect(() => {
    if (!value) {
      setStatus('idle');
      return;
    }
    setStatus('checking');
    const t = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/checklist-templates/${encodeURIComponent(value)}`,
          { credentials: 'include' },
        );
        if (res.status === 404) {
          setStatus('free');
        } else if (res.ok) {
          const body = (await res.json()) as { template?: { id?: string } };
          if (excludeId && body.template?.id === excludeId) setStatus('free');
          else setStatus('taken');
        } else {
          setStatus('idle');
        }
      } catch {
        setStatus('idle');
      }
    }, 400);
    return () => window.clearTimeout(t);
  }, [value, excludeId]);

  return (
    <div className="space-y-1.5">
      <Label
        htmlFor="slug-input"
        className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground"
      >
        Slug
      </Label>
      <Input
        id="slug-input"
        value={value}
        onChange={(e) => {
          setEdited(true);
          onChange(slugify(e.target.value));
        }}
        className="h-12 font-mono"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-testid="slug-input"
      />
      {status === 'checking' ? (
        <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          Checking…
        </p>
      ) : null}
      {status === 'taken' ? (
        <p className="text-sm text-destructive" data-testid="slug-taken">
          Already in use.
        </p>
      ) : null}
    </div>
  );
}
